import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EmailNameForm from './EmailNameForm.js';
import { validateNameEmail } from './emailSignup.js';

// "Subscribe" CTA for the News page filter row. On click it opens a modal
// (matching the News EditModal pattern) with the shared name + email form.
// Submits to POST /api/news-maillist/subscribe — a DOUBLE OPT-IN flow: the
// backend stores a `pending` row and emails a confirmation link; the row only
// becomes `confirmed` after the user clicks it. So success here means "check
// your email", not "subscribed".
const NEWS_BUTTON_STYLE = {
  backgroundColor: '#4a7094', color: '#fff',
  border: 'none', borderRadius: '10px', padding: '10px 20px',
  fontFamily: 'Inter, sans-serif', fontWeight: '600',
  fontSize: '14px', cursor: 'pointer',
};

const NewsSubscribeButton = ({ loggedInUser }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Prefill from the logged-in account when available.
  useEffect(() => {
    if (loggedInUser) {
      if (loggedInUser.name) setName(loggedInUser.name);
      if (loggedInUser.email) setEmail(loggedInUser.email);
    }
  }, [loggedInUser]);

  const closeModal = () => {
    setOpen(false);
    setError('');
    setSuccessMsg('');
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const valid = validateNameEmail(name, email);
    if (!valid.ok) {
      setError(valid.error);
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/news-maillist/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: valid.email, name: valid.name }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccessMsg(data.message || "Almost done! Check your email to confirm your subscription.");
      } else {
        setError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      console.error('News subscribe error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" style={NEWS_BUTTON_STYLE} onClick={() => setOpen(true)}>
        ✉ Subscribe
      </button>

      {open && ReactDOM.createPortal(
        <div
          onClick={closeModal}
          style={{
            // Rendered via portal to document.body so it escapes the sticky
            // filter row's stacking context (zIndex 500); z-index 2000 sits
            // above the sticky header (1000) so the whole modal is visible.
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '40px 20px', zIndex: 2000, overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Subscribe to news and updates"
            style={{
              backgroundColor: '#81B2CA', borderRadius: '20px', padding: '30px',
              width: '100%', maxWidth: '440px', display: 'flex',
              flexDirection: 'column', gap: '16px', alignItems: 'center',
            }}
          >
            <h2 style={{ fontFamily: 'Inter, sans-serif', margin: 0, fontSize: '22px', color: '#000', textAlign: 'center' }}>
              Get Vicktoria AI™ News &amp; Updates
            </h2>

            {successMsg ? (
              <>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#000', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                  {successMsg}
                </p>
                <button type="button" style={NEWS_BUTTON_STYLE} onClick={closeModal}>
                  Close
                </button>
              </>
            ) : (
              <>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#222', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                  Enter your name and email. We'll send a confirmation link — click it to finish subscribing.
                </p>
                <EmailNameForm
                  name={name}
                  email={email}
                  onNameChange={setName}
                  onEmailChange={setEmail}
                  error={error}
                  submitting={submitting}
                  submitLabel="Subscribe"
                  submittingLabel="Subscribing…"
                  submitStyle={NEWS_BUTTON_STYLE}
                  onSubmit={handleSubmit}
                />
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    background: 'none', border: 'none', color: '#2B1E19',
                    fontFamily: 'Inter, sans-serif', fontSize: '13px',
                    cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default NewsSubscribeButton;
